/**
 * Admin → Providers (carrier configuration). Platform-only — the
 * backend gates with RequirePlatform middleware, so non-platform
 * tenants will see 403. The sidebar item filters on tenantType too,
 * so resellers / customers don't see the link in the first place.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type ProviderProtocol = 'http_rest' | 'smpp' | 'sip';
export type ProviderChannel = 'sms' | 'voice' | 'both';
export type ProviderHealthStatus = 'unknown' | 'up' | 'down';

export interface Provider {
  id: number;
  name: string;
  protocol: ProviderProtocol;
  channel: ProviderChannel;
  config: Record<string, unknown>;
  /** True when secret_config_encrypted is non-null in the DB. */
  secrets_configured: boolean;
  is_active: boolean;
  /**
   * When true (default), the customer is refunded on a failed delivery
   * (failed-DLR + stuck-'sent' sweep timeout). Set false for carriers
   * that bill us for every accepted message regardless of delivery
   * (e.g. Infobip) so we don't eat the carrier cost. Independent of DLR
   * support. See migration 000114.
   */
  refund_on_failure: boolean;
  health_status: ProviderHealthStatus;
  consecutive_failures: number;
  last_health_check_at?: string;
  /** Auto-disable knobs (per-provider; the worker reads them each tick). */
  auto_disable_enabled: boolean;
  auto_disable_threshold: number; // 0..1
  auto_disable_min_sample: number;
  auto_disable_window_minutes: number;
  /** Set by the auto-disable worker when it pulls the carrier out of rotation. */
  last_auto_disabled_at?: string;
  last_auto_disable_reason?: string;
  created_at: string;
}

/** Edit-form variant — includes decrypted secrets. Returned by GET /{id}. */
export interface ProviderWithSecrets extends Provider {
  secrets: Record<string, string>;
}

export interface ProvidersResponse {
  items: Provider[];
}

export interface CreateProviderInput {
  name: string;
  protocol: ProviderProtocol;
  channel: ProviderChannel;
  config: Record<string, unknown>;
  secrets: Record<string, string>;
  is_active: boolean;
  /** Defaults TRUE server-side when omitted. See migration 000114. */
  refund_on_failure: boolean;
}

export interface UpdateProviderInput {
  protocol: ProviderProtocol;
  channel: ProviderChannel;
  config: Record<string, unknown>;
  /**
   * Three-state contract:
   * - undefined → keep existing encrypted blob (operator edited only metadata)
   * - {} → clear secrets
   * - { k: v } → replace + re-encrypt
   */
  secrets?: Record<string, string>;
  is_active: boolean;
  /** Patch-style like is_active — the edit form always sends it. */
  refund_on_failure: boolean;
}

export interface ProbeResult {
  ok: boolean;
  latency_ms: number;
  error?: string;
}

const PROVIDERS_KEY = ['admin', 'providers'] as const;

/**
 * `enabled: false` skips the request entirely. Callers that render for
 * resellers must pass it: the endpoint is platform-only, so firing it
 * from a reseller session just burns a guaranteed 403.
 */
export function useProviders(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: () => api.get<ProvidersResponse>('/v1/admin/providers'),
    enabled: opts.enabled ?? true,
    // Health badges become stale fast (the worker writes every 30s);
    // 15s keeps the UI within ~half a tick of reality without polling.
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useProviderForEdit(id: number | null) {
  return useQuery({
    queryKey: ['admin', 'providers', id, 'edit'] as const,
    queryFn: () => api.get<ProviderWithSecrets>(`/v1/admin/providers/${id}`),
    enabled: id !== null && id > 0,
    // Secrets shouldn't be cached longer than necessary — refetch on
    // every modal open so a recent UPDATE elsewhere is reflected.
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProviderInput) =>
      api.post<Provider>('/v1/admin/providers', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}

export function useUpdateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateProviderInput }) =>
      api.patch<Provider>(`/v1/admin/providers/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/admin/providers/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}

export function useProbeProvider() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ProbeResult>(`/v1/admin/providers/${id}/probe`, {}),
  });
}

// Smoke test — fires a real send through the carrier and returns the
// response inline. Distinct from probe (HealthCheck): smoke validates
// the full SEND path including sender_id authorization at the carrier.
// Costs the operator real carrier credit; the modal makes that
// explicit before submit.
export interface SmokeTestRequest {
  /** Optional — falls back to the provider's configured default
   *  sender (e.g. Twilio's `from_number`) when empty. */
  from?: string;
  to: string;
  body: string;
}

export interface SmokeTestResult {
  ok: boolean;
  /** Carrier-assigned id when the send succeeded; useful for support
   *  tickets if the SMS doesn't actually arrive. */
  provider_msg_id?: string;
  http_status: number;
  error_code?: string;
  error_message?: string;
  latency_ms: number;
  /** Human-readable summary of the request the API just made
   *  (URL + from + to). Helps the operator verify what was sent. */
  request_summary: string;
}

export function useSmokeTestProvider() {
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: SmokeTestRequest }) =>
      api.post<SmokeTestResult>(`/v1/admin/providers/${id}/smoke-test`, input),
  });
}

// Wholesale balance — OUR prepaid credit at the carrier (e.g. our Infobip
// account balance), NOT any per-tenant balance. Platform-only; the
// endpoint is gated by RequirePlatform server-side and the UI only renders
// the trigger for the platform super-admin.
export interface ProviderBalance {
  provider_id: number;
  provider: string;
  balance: number;
  currency: string;
  /** RFC3339 timestamp of when the API read the balance from the carrier. */
  checked_at: string;
}

/**
 * Lazy, on-demand read of a provider's wholesale balance. Disabled by
 * default (no auto-poll, no fetch on mount) — the caller triggers it with
 * `.refetch()` when the operator clicks "Check balance". Not every carrier
 * supports it: unsupported providers return 501 balance_not_supported,
 * which surfaces here as an ApiError the UI can render inline.
 */
export function useProviderBalance(id: number) {
  return useQuery({
    queryKey: ['admin', 'providers', id, 'balance'] as const,
    queryFn: () => api.get<ProviderBalance>(`/v1/admin/providers/${id}/balance`),
    enabled: false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

// Billing reconciler — joins the carrier's per-message billing log against
// OUR messages + transactions to surface under-billing (margin leak), DLR
// drift, and the carrier's real per-destination rate. Platform-only; the
// endpoint is gated by RequirePlatform server-side and the UI only renders
// the panel for the platform super-admin.
export interface ReconcileProblemRow {
  message_id: string;
  classification: 'matched_ok' | 'under_billed' | 'orphan';
  status_drift: boolean;
  mcc_mnc: string;
  our_status: string;
  their_status: string;
  our_charged: boolean;
  our_sell_price: number;
  their_price: number;
}

export interface DestinationRate {
  mcc_mnc: string;
  lines: number;
  their_avg_rate: number;
  their_min_rate: number;
  their_max_rate: number;
  our_avg_cost: number;
  our_cost_resolved: boolean;
  rate_mismatch: boolean;
}

export interface ReconcileReport {
  provider_id: number;
  provider: string;
  sent_since: string;
  sent_until: string;
  currency: string;
  priced_lines: number;
  matched_ok: number;
  under_billed: number;
  orphans: number;
  status_drifts: number;
  under_billed_carrier_cost: number;
  under_billed_expected_sell: number;
  problems_total: number;
  problem_rows: ReconcileProblemRow[];
  rate_table: DestinationRate[];
}

export interface ReconcileParams {
  /** RFC3339 window start. */
  since: string;
  /** RFC3339 window end. Window must be ≤48h (Infobip log retention). */
  until: string;
}

/**
 * Lazy, on-demand billing reconciliation over a bounded window. Runs only
 * when the operator clicks "Run reconciliation" (mutation, no auto-poll).
 * A mutation rather than a query so the date range is an explicit input and
 * nothing fires on mount.
 */
export function useProviderReconcile(id: number) {
  return useMutation({
    mutationFn: ({ since, until }: ReconcileParams) =>
      api.get<ReconcileReport>(
        `/v1/admin/providers/${id}/reconcile?since=${encodeURIComponent(since)}&until=${encodeURIComponent(until)}`,
      ),
  });
}

/**
 * Manual recovery from an auto-disable. Flips is_active back to TRUE.
 * The audit fields (last_auto_disabled_at + reason) are preserved
 * server-side so the operator can still see what triggered the disable.
 */
export function useReactivateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Provider>(`/v1/admin/providers/${id}/reactivate`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROVIDERS_KEY });
    },
  });
}
